import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  Calculator,
  Contact,
  FolderOpen,
  Megaphone,
  Users,
  type LucideIcon
} from 'lucide-react';
import { Badge, Button, Card, Skeleton } from '@bliks-io/ui';
import type { PartnerMaterial, PartnerMaterialLocale } from '@bliks-io/contracts';
import { useProfile } from '@/api/queries/useProfile';
import { usePartnerNews } from '@/pages/partner/api/queries/usePartnerNews';
import { usePartnerMaterials } from '@/pages/partner/api/queries/usePartnerMaterials';
import { PartnerFileFormatIcon } from '@/components/partner-content/PartnerFileFormat';
import { PartnerInfoPanel } from '@/pages/partner/components/PartnerInfoPanel';
import { PartnerNewsStory, toNewsSlides } from '@/components/partner-content/PartnerNewsStory';
import { NewLeadButton } from '@/pages/partner/NewLeadButton';
import {
  PARTNER_MATERIAL_CATEGORY_KEYS,
  isRecentlyChanged,
  pickPartnerMaterialText,
  toPartnerMaterialLocale
} from '@/lib/partner-material-text';
import { formatDate } from '@/lib/formatters';

const RECENT_MATERIAL_LIMIT = 5;

interface QuickLink {
  to: '/partner/leads' | '/partner/contacts' | '/partner/info-hub' | '/partner/calculator';
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
}

const QUICK_LINKS: QuickLink[] = [
  {
    to: '/partner/leads',
    icon: Users,
    titleKey: 'partner.home.links.leads.title',
    descriptionKey: 'partner.home.links.leads.description'
  },
  {
    to: '/partner/contacts',
    icon: Contact,
    titleKey: 'partner.home.links.contacts.title',
    descriptionKey: 'partner.home.links.contacts.description'
  },
  {
    to: '/partner/info-hub',
    icon: FolderOpen,
    titleKey: 'partner.home.links.infoHub.title',
    descriptionKey: 'partner.home.links.infoHub.description'
  },
  {
    to: '/partner/calculator',
    icon: Calculator,
    titleKey: 'partner.home.links.calculator.title',
    descriptionKey: 'partner.home.links.calculator.description'
  }
];

/** Newest changes first, so the home page shows what partners have not seen yet. */
export function recentMaterials(materials: PartnerMaterial[], limit = RECENT_MATERIAL_LIMIT) {
  return [...materials]
    .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
    .slice(0, limit);
}

export function PartnerHomePage() {
  const { t, i18n } = useTranslation();
  const { data: profile } = useProfile();
  const locale = toPartnerMaterialLocale(i18n.language);

  return (
    <div className="flex h-full flex-col gap-8 overflow-auto p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {profile?.firstName
              ? t('partner.home.greeting', { name: profile.firstName })
              : t('partner.home.greetingAnonymous')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('partner.home.subtitle')}</p>
        </div>
        <NewLeadButton />
      </div>

      <nav
        aria-label={t('partner.home.quickLinks')}
        className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        {QUICK_LINKS.map((link) => (
          <QuickLinkCard key={link.to} link={link} />
        ))}
      </nav>

      <div className="grid gap-8 lg:grid-cols-3">
        <section className="flex flex-col gap-4 lg:col-span-2" aria-labelledby="partner-news">
          {/* Same row height as the other column's header, whose button sets it,
              so both cards start level. */}
          <div className="flex min-h-11 items-center">
            <h2 id="partner-news" className="text-lg font-medium text-foreground">
              {t('partner.home.news.title')}
            </h2>
          </div>
          <NewsFeed locale={locale} />
        </section>
        <section className="flex flex-col gap-4" aria-labelledby="partner-recent-material">
          <div className="flex min-h-11 items-center justify-between">
            <h2 id="partner-recent-material" className="text-lg font-medium text-foreground">
              {t('partner.home.recentMaterial.title')}
            </h2>
            <Button variant="ghost" size="small" asChild>
              <Link to="/partner/info-hub">
                {t('partner.home.recentMaterial.all')}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <RecentMaterial locale={locale} />
        </section>
      </div>
    </div>
  );
}

function QuickLinkCard({ link }: { link: QuickLink }) {
  const { t } = useTranslation();
  const Icon = link.icon;
  return (
    <Link
      to={link.to}
      className="group rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card>
        <div className="flex items-start gap-3 p-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
            <Icon className="h-5 w-5 text-foreground" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground">{t(link.titleKey)}</p>
            <p className="text-sm text-muted-foreground">{t(link.descriptionKey)}</p>
          </div>
          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </div>
      </Card>
    </Link>
  );
}

function NewsFeed({ locale }: { locale: PartnerMaterialLocale }) {
  const { t } = useTranslation();
  const { data: news = [], isLoading, isError } = usePartnerNews();

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (isError) {
    return <p className="text-sm text-muted-foreground">{t('partner.home.news.error')}</p>;
  }
  if (news.length === 0) {
    return (
      <Card>
        <PartnerInfoPanel icon={Megaphone} title={t('partner.home.news.empty')} className="py-10" />
      </Card>
    );
  }
  return <PartnerNewsStory slides={toNewsSlides(news)} locale={locale} />;
}

function RecentMaterial({ locale }: { locale: PartnerMaterialLocale }) {
  const { t } = useTranslation();
  const { data: materials = [], isLoading, isError } = usePartnerMaterials();

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (isError) {
    return <p className="text-sm text-muted-foreground">{t('partner.infoHub.errorDescription')}</p>;
  }
  const recent = recentMaterials(materials);
  if (recent.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('partner.infoHub.empty')}</p>;
  }
  return (
    <Card>
      <ul className="flex flex-col divide-y">
        {recent.map((material) => (
          <li key={material._id}>
            <Link
              to="/partner/info-hub"
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50"
            >
              <PartnerFileFormatIcon format={Object.values(material.files)[0]?.format} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {pickPartnerMaterialText(material.title, locale)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(PARTNER_MATERIAL_CATEGORY_KEYS[material.category])} ·{' '}
                  {formatDate(material.changedAt)}
                </p>
              </div>
              {isRecentlyChanged(material.changedAt) && (
                <Badge colorScheme="success" size="sm">
                  {t('partner.infoHub.new')}
                </Badge>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
